import { useCallback, useMemo, useState } from "react";
import type { PublicUpdate } from "@verity-connect/contracts";

const STORAGE_KEY = "wvcea-update-read-state-v1";
/**
 * Archiving is tidying, not deleting. WVCEA sent the notice, WVCEA's record of sending
 * it stands, and a teacher clearing their list must not be able to make an official
 * message disappear — it moves behind a line at the bottom and can come back.
 *
 * Kept on the device, like reading. A person who tidies on their phone and then opens an
 * iPad sees an untidy list, which costs them nothing; a sync path misfiring during
 * convention week costs more than the tidiness is worth.
 */
const ARCHIVE_KEY = "wvcea-update-archive-v1";

export function updateReadKey(update: Pick<PublicUpdate, "id" | "publishedAt">): string {
  return `${update.id}:${update.publishedAt}`;
}

/** The notices still in somebody's list. Archived ones are hidden, never removed. */
export function unarchivedUpdates(updates: readonly PublicUpdate[], archivedIds: ReadonlySet<string>): readonly PublicUpdate[] {
  return updates.filter((update) => !archivedIds.has(update.id));
}

export function unreadUpdateIds(updates: readonly PublicUpdate[], readKeys: ReadonlySet<string>): string[] {
  return updates.filter((update) => !readKeys.has(updateReadKey(update))).map((update) => update.id);
}

export function useUpdateReadState(updates: readonly PublicUpdate[]) {
  const [readKeys, setReadKeys] = useState<ReadonlySet<string>>(() => readStoredKeys());
  const [archivedIds, setArchivedIds] = useState<ReadonlySet<string>>(() => readStoredSet(ARCHIVE_KEY));
  // An archived notice stops counting. Somebody who has put it away has dealt with it,
  // and a badge that keeps insisting otherwise teaches people to ignore the badge.
  const unreadIds = useMemo(
    () => unreadUpdateIds(updates, readKeys).filter((id) => !archivedIds.has(id)),
    [archivedIds, readKeys, updates],
  );
  const markRead = useCallback((updateId: string) => {
    const update = updates.find((candidate) => candidate.id === updateId);
    if (!update) return;
    setReadKeys((current) => {
      const key = updateReadKey(update);
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      writeStoredKeys(next);
      return next;
    });
  }, [updates]);
  const markAllRead = useCallback(() => {
    setReadKeys((current) => {
      const next = new Set(current);
      for (const update of updates) next.add(updateReadKey(update));
      writeStoredKeys(next);
      return next;
    });
  }, [updates]);
  const setArchived = useCallback((updateId: string, archived: boolean) => {
    setArchivedIds((current) => {
      if (current.has(updateId) === archived) return current;
      const next = new Set(current);
      if (archived) next.add(updateId); else next.delete(updateId);
      writeStoredSet(ARCHIVE_KEY, next);
      return next;
    });
  }, []);
  return { unreadIds, unreadCount: unreadIds.length, markRead, markAllRead, archivedIds, setArchived };
}

function readStoredKeys(): ReadonlySet<string> {
  return readStoredSet(STORAGE_KEY);
}

function readStoredSet(key: string): ReadonlySet<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

function writeStoredSet(key: string, values: ReadonlySet<string>): void {
  try { localStorage.setItem(key, JSON.stringify([...values].slice(-500))); } catch { /* Kept in memory when device storage is unavailable. */ }
}

function writeStoredKeys(keys: ReadonlySet<string>): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys].slice(-500))); } catch { /* Unread state remains in memory when device storage is unavailable. */ }
}
